import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MEITRE_GA_ID } from '@core/config/constants';
import { getAnalyticsEnvironment, ANALYTICS_ENV } from './getEnvironment';
import { loadGA4 } from './scriptLoaders';
import type { AnalyticsContextValue } from './types';
import { createAnalyticsTrackers } from './createAnalyticsTrackers';
import { AnalyticsContext } from './analyticsContext';

interface GroupAnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * Group-scoped analytics provider. Same context shape as AnalyticsProvider so
 * RouteTracker and useAnalytics work unchanged, but seeded from the global
 * Meitre GA4 — no restaurant/tenant.
 *
 * Per-group GTM is loaded in GroupLandingPage (after group data arrives),
 * not here, because this provider mounts above the router before any group is
 * fetched — gtmKey would always be undefined at this point.
 *
 * trackConversion is a no-op: group landing pages have no booking conversion.
 */
export function GroupAnalyticsProvider({ children }: GroupAnalyticsProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const envRef = useRef(getAnalyticsEnvironment());

  useEffect(() => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.PRODUCTION) {
      // Global Meitre GA4 (no auto page_view — RouteTracker handles it)
      loadGA4(MEITRE_GA_ID, false);
    }

    setIsInitialized(true);
  }, []);

  // Shared page_view / event trackers — same factory as AnalyticsProvider, but
  // tagged 'v2-group' and with no secondary GA id, so page_view single-sends to
  // the Meitre GA only. Memoized for referential stability of the context value.
  const { trackPageView, trackEvent } = useMemo(
    () => createAnalyticsTrackers({ envRef, clientTag: 'v2-group' }),
    [],
  );

  const value: AnalyticsContextValue = useMemo(() => ({
    trackEvent,
    trackPageView,
    // Group landing pages have no booking conversion — no-op satisfies the
    // shared AnalyticsContextValue contract without emitting any events.
    trackConversion: () => {},
    isInitialized,
  }), [trackEvent, trackPageView, isInitialized]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}
